"use client"

import React from "react"

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";

import Assignment from "@mui/icons-material/Assignment";
import ChevronRight from "@mui/icons-material/ChevronRight";

import type { TreeNode, FolderStatus, FileStatus } from "../../file-tree-manager/types/tree-types"
import { } from "react"
import { getFolderStatusInfo, getFileStatusInfo } from "../../file-tree-manager/utils/status-utils"

interface StatusManagerProps {
  node: TreeNode
  onStatusChange: (status: FolderStatus | FileStatus) => void
}

export const StatusManager: React.FC<StatusManagerProps> = ({ node, onStatusChange }) => {
  let isFolder = (node.type === "folder") ? true : false
  const statusInfo = isFolder
    ? getFolderStatusInfo(node.status as FolderStatus)
    : getFileStatusInfo(node.status as FileStatus)

  const flowConfig = node.flowConfig || null

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Assignment sx={{ mr: 1, color: "text.secondary" }} />
        <Typography variant="subtitle2">상태:</Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Chip
            label={statusInfo.label}
            size="small"
            sx={{
              backgroundColor: statusInfo.bgColor,
              color: statusInfo.color,
              fontWeight: "medium",
            }}
          />
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
            상태 변경:
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
            {(() => {
              const currentStatus = (node.status || "draft") as FolderStatus | FileStatus
              let flow: (FolderStatus | FileStatus)[]

              if (flowConfig) {
                // 두 가지 포맷 지원: 배열 또는 맵
                if (isFolder) {
                  const maybeArray = (flowConfig as any).folder as FolderStatus[] | Record<string, FolderStatus[]> | undefined
                  if (Array.isArray(maybeArray)) {
                    const arr = maybeArray as FolderStatus[]
                    const idx = Math.max(0, arr.indexOf(currentStatus as FolderStatus))
                    flow = arr
                  } else {
                    const folderMap = (maybeArray || {}) as Record<string, FolderStatus[]>
                    flow = folderMap[currentStatus as string]
                  }
                } else {
                  const maybeArray = (flowConfig as any).file as FileStatus[] | Record<string, FileStatus[]> | undefined
                  if (Array.isArray(maybeArray)) {
                    flow = maybeArray as FileStatus[]
                  } else {
                    const fileMap = (maybeArray || {}) as Record<string, FileStatus[]>
                    flow = fileMap[currentStatus as string]
                  }
                }
              } else {
                  flow = ["draft"]
              }

              const currentIndex = flow.indexOf(currentStatus)

              return flow
                .map((statusKey, idx, arr) => {
                  const info = isFolder
                    ? getFolderStatusInfo(statusKey as FolderStatus)
                    : getFileStatusInfo(statusKey as FileStatus)
                  const isCurrent = statusKey === currentStatus
                  const isNext = idx === arr.length - 1 // 마지막 항목(=현재 상태)이면 다음 없음
                  // disabled는 현재 상태가 아니고 버튼이 아니면 비활성화
                  return (
                    <Box key={String(statusKey)} sx={{ display: "flex", alignItems: "center" }}>
                      <Button
                        size="small"
                        variant={"contained"}
                        sx={{
                          backgroundColor: (!(isNext && info.isButton) ? info.bgColor : info.color),
                          color: (!(isNext && info.isButton) ? info.color : info.bgColor),
                          fontWeight: "medium",
                          '&:hover': {
                            backgroundColor: (!(isNext && info.isButton) ? info.bgColor : info.color),
                          },
                          "&.Mui-disabled": {
                            background: (!(isNext && info.isButton) ? info.bgColor : info.color),
                            color: (!(isNext && info.isButton) ? info.color : info.bgColor),
                          },
                        }}
                        onClick={() => onStatusChange(statusKey as FolderStatus | FileStatus)}
                        disabled={!info.isButton}
                      >
                        {info.label}
                      </Button>
                      {idx < arr.length - 1 && (
                        <ChevronRight fontSize="small" sx={{ color: "text.disabled", mx: 0.5 }} />
                      )}
                    </Box>
                  )
                })
            })()}
          </Box>
        </Box>

        {/* 상태별 설명 */}
        <Box sx={{ mt: 2, p: 1, bgcolor: "grey.50", borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {isFolder ? (
              <>
                <strong>부서 상태:</strong> 초안 → 배포 승인 요청 → 배포 완료
              </>
            ) : (
              <>
                <strong>컨텐츠 상태:</strong> 초안 → Markdown 변환 → 담당자 리뷰 → 배포 승인 요청 → 배포 완료
              </>
            )}
          </Typography>
        </Box>
      </Paper>
    </Box>
  )
}